import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout, { refreshAdminNotifs } from '../../components/TrackerLayout';
import PaymentDateModal from '../../components/PaymentDateModal';
import ReceiptModal from '../../components/ReceiptModal';
import { useTracker } from '../../context/TrackerContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  deduplicatePayments, deletePaymentRecord, getPaymentData,
  getSalespeople, markRunAway, setPaymentClassOver, updatePaymentRecord,
  markIncentivePaid, DATED_PAYMENT_STATUSES,
} from '../../lib/api';
import type { NextPayment, PaymentRecord } from '../../lib/api';
import {
  presetPeriod, periodFromParams, periodParams, within, distinctNames, norm, fmtRange, fmtDay,
  PeriodPicker, FilterCard, SearchFilter, SelectFilter, type Period, type ActiveFilter,
} from '../../components/ReportKit';

// ── Helpers ──────────────────────────────────────────────────────────

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN');
const fmtShortDay = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const todayIST = () => new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);

// The agreed next-payment date, if the student has one (see Payment Dates page)
const dueDateOf = (r: PaymentRecord) =>
  r.nextPayment && DATED_PAYMENT_STATUSES.includes(r.nextPayment.status) ? r.nextPayment.dueDate : '';

// Small chip under the Due amount: the next payment date, or where asking it is
// at — click it to change the date, approve it, or ask the student. A student
// who still owes and has nothing yet gets a "Set date" chip.
const NextPaymentChip: React.FC<{ r: PaymentRecord; today: string; onOpen: () => void }> = ({ r, today, onOpen }) => {
  const np = r.nextPayment;
  if (!np && !canDate(r)) return null;
  const due = dueDateOf(r);
  const [label, bg, color, title] =
    !np ? ['📅 set date', '#ede9fe', '#5b21b6', 'No next payment date yet — ask the student or set it']
    : due ? (due < today ? [`⏰ was due ${fmtShortDay(due)}`, '#fee2e2', '#b91c1c', 'Payment overdue — click to change the date']
         : due === today ? ['📅 due today', '#dcfce7', '#15803d', 'Payment due today — click to change the date']
         : [`📅 ${fmtShortDay(due)}`, '#f3f4f6', '#4b5563', 'Next payment date — click to change it'])
    : np.status === 'Pending' ? [`🙋 asked ${fmtShortDay(np.requestedDate)}`, '#fef3c7', '#92400e', 'The student chose this date — click to approve or give another']
    : ['⌛ date asked', '#f3f4f6', '#6b7280', "Asked for their next payment date — they haven't chosen one yet. Click to remind them or set it"];
  return (
    <button onClick={onOpen} title={title}
      className="block mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full w-fit whitespace-nowrap hover:ring-2 hover:ring-offset-1 hover:ring-purple-200"
      style={{ background: bg, color }}>
      {label}
    </button>
  );
};

// Session type → display label + default fee
// DB stores: 'group' = Offline batch, 'one-to-one' = Online 1:1
// Modal stores: 'online' | 'offline' (after edit)
type SessionMode = 'online' | 'offline' | 'one-to-one' | 'group';
const SESSION_FEE: Record<string, number> = {
  online: 15000, offline: 20000, group: 20000, 'one-to-one': 15000,
};
const normalizeSession = (s: string): 'online' | 'offline' => {
  const v = (s || '').toLowerCase();
  if (v === 'online' || v === 'one-to-one') return 'online';
  return 'offline'; // 'offline', 'group', or unknown → Offline
};

// The server works the mode out (online Pay After Placement sign-ups are
// stored as 'group' + an 'online' category); older servers don't send it
const modeOf = (r: PaymentRecord) => r.mode ?? normalizeSession(r.sessionType || r.sessionCategory);

// The search box: name, phone, email, course, sales person or mentor.
// Phone numbers match with or without spaces.
function matchesSearch(r: PaymentRecord, search: string) {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const text = [r.name, r.phone, r.email, r.studentEmail, r.course, r.salesPerson, r.mentorName, r.studentId].join(' ').toLowerCase();
  if (text.includes(q)) return true;
  const digits = q.replace(/\D/g, '');
  return digits.length >= 3 && (r.phone || '').replace(/\D/g, '').includes(digits);
}

// The salesperson filter: whose student it is ('none' = no salesperson)
const matchesSales = (r: PaymentRecord, sales: string) =>
  !sales || (sales === 'none' ? !norm(r.salesPerson) : norm(r.salesPerson) === norm(sales));

// Joining date as YYYY-MM-DD (older servers only send the raw enrollDate)
function joinedOf(r: PaymentRecord) {
  if (r.joined !== undefined) return r.joined;
  if (/^\d{4}-\d{2}-\d{2}/.test(r.enrollDate || '')) return r.enrollDate.slice(0, 10);
  const d = new Date(r.enrollDate);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

// Can a next payment date be asked for or set? (The server checks the same.)
const canDate = (r: PaymentRecord) =>
  r.status !== 'Ran Away' && r.courseFee > 0 && r.balance > 0 && r.studentId !== '';

const STATUS_STYLE: Record<string, { bg: string; color: string; dot: string }> = {
  'Fully Paid': { bg: '#dcfce7', color: '#15803d', dot: '#16a34a' },
  'Partial':    { bg: '#fef9c3', color: '#854d0e', dot: '#d97706' },
  'Ran Away':   { bg: '#fce7f3', color: '#9d174d', dot: '#db2777' },
  'Class Over': { bg: '#e0f2fe', color: '#0369a1', dot: '#0284c7' },
};

// ── Mini stat tile ────────────────────────────────────────────────────

const Tile: React.FC<{
  label: string; value: string | number; sub?: string;
  accent?: string; icon: string; active?: boolean; onClick?: () => void;
}> = ({ label, value, sub, accent = '#2b2240', icon, active, onClick }) => (
  <div onClick={onClick}
    className={`relative bg-white rounded-2xl border p-4 shadow-sm transition-all overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${active ? 'ring-2 ring-offset-1' : ''}`}
    style={{ borderColor: active ? accent : '#e5e7eb', ...(active ? { '--tw-ring-color': accent } as any : {}) }}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: '#9ca3af' }}>{label}</p>
        <p className="text-2xl font-black leading-none" style={{ color: accent }}>{value}</p>
        {sub && <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">{sub}</p>}
      </div>
      <span className="text-2xl flex-shrink-0 opacity-80">{icon}</span>
    </div>
    {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ background: accent }} />}
  </div>
);

// ── Revenue bar ───────────────────────────────────────────────────────

const RevBar: React.FC<{ label: string; value: number; total: number; color: string; icon: string }> = ({ label, value, total, color, icon }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{icon} {label}</span>
        <span className="text-xs font-bold" style={{ color }}>{pct}%</span>
      </div>
      <div className="text-xl font-black mb-1.5" style={{ color }}>{fmt(value)}</div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

// ── Update Modal ──────────────────────────────────────────────────────

const UpdateModal: React.FC<{
  record: PaymentRecord;
  adminPassword: string;
  allSalespeople: string[];
  onClose: () => void;
  onSaved: (rowIndex: string, balancePaid: number, newTotal: number, sessionType: string, salesPerson: string, courseFee: number, enrollDate: string) => void;
}> = ({ record, adminPassword, allSalespeople, onClose, onSaved }) => {
  const [amount, setAmount]       = useState(record.balancePaid > 0 ? String(record.balancePaid) : '');
  const [session, setSession]     = useState<'online' | 'offline'>(modeOf(record));
  const [salesPerson, setSalesPerson] = useState(record.salesPerson || '');
  const [customFee, setCustomFee] = useState(String(record.courseFee || SESSION_FEE[modeOf(record)]));
  const [enrollDate, setEnrollDate] = useState(record.enrollDate || '');
  const [saving, setSaving]       = useState(false);

  const courseFee    = Number(customFee) > 0 ? Number(customFee) : SESSION_FEE[session];
  const balancePaid  = Number(amount) || 0;
  const newTotal     = record.regPaid + balancePaid;
  const remaining    = Math.max(0, courseFee - newTotal);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await updatePaymentRecord(adminPassword, record.rowIndex, balancePaid, session, salesPerson, courseFee, record.regPaid, enrollDate);
      onSaved(record.rowIndex, balancePaid, res.newTotal, session, salesPerson, courseFee, enrollDate);
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden">
        {/* Modal header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
          <div>
            <h3 className="font-bold text-white">Update Payment</h3>
            <p className="text-xs text-white/60 mt-0.5">{record.name}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg leading-none">×</button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {/* Student snapshot */}
          <div className="rounded-xl border border-gray-100 p-3.5 mb-5 grid grid-cols-3 gap-3 text-center" style={{ background: '#f8f6ff' }}>
            {[
              { label: 'Reg Paid', value: fmt(record.regPaid), color: '#16a34a' },
              { label: 'Total Paid', value: fmt(record.totalPaid), color: '#2b2240' },
              { label: 'Balance Due', value: remaining === 0 ? '✓ Clear' : fmt(remaining), color: remaining === 0 ? '#16a34a' : '#dc2626' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">{s.label}</div>
                <div className="text-sm font-bold" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            {/* Session type */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Session Mode</label>
              <div className="grid grid-cols-2 gap-2">
                {([['online', 'Online', '₹15,000'], ['offline', 'Offline', '₹20,000']] as const).map(([key, label, price]) => (
                  <button key={key} type="button" onClick={() => { setSession(key); setCustomFee(String(SESSION_FEE[key])); }}
                    className="py-3 rounded-xl text-xs font-bold border-2 transition-all flex flex-col items-center gap-0.5"
                    style={session === key
                      ? { background: '#2b2240', color: '#F0B429', borderColor: '#2b2240' }
                      : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
                    <span>{label}</span>
                    <span className="text-[10px] opacity-70">{price}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Joining date */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Joining Date</label>
              <input type="date" value={enrollDate} onChange={e => setEnrollDate(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400" />
            </div>

            {/* Course fee */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Course Fee (₹)</label>
              <input type="number" min="0" value={customFee} onChange={e => setCustomFee(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400" />
              <p className="text-[10px] text-gray-400 mt-1">Override for custom arrangements. Online default ₹15k, Offline ₹20k.</p>
            </div>

            {/* Salesperson */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Sales Person</label>
              <select value={salesPerson} onChange={e => setSalesPerson(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 bg-white">
                <option value="">— None —</option>
                {salesPerson && !allSalespeople.includes(salesPerson) && (
                  <option value={salesPerson}>{salesPerson}</option>
                )}
                {allSalespeople.map(sp => <option key={sp} value={sp}>{sp}</option>)}
              </select>
            </div>

            {/* Balance paid */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Balance Payment Received (₹)</label>
              <input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder={`e.g. ${Math.max(0, courseFee - record.regPaid)}`}
                autoFocus
                className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:border-yellow-400" />
            </div>

            {/* Live breakdown */}
            <div className="rounded-xl border-2 border-dashed border-gray-200 p-4 space-y-2">
              {[
                { label: 'Course Fee', value: fmt(courseFee), color: '#2b2240' },
                { label: 'Registration Paid', value: fmt(record.regPaid), color: '#16a34a' },
                { label: 'Balance Paid', value: fmt(balancePaid), color: '#16a34a' },
              ].map(row => (
                <div key={row.label} className="flex justify-between text-sm">
                  <span className="text-gray-500">{row.label}</span>
                  <span className="font-semibold" style={{ color: row.color }}>{row.value}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm border-t border-gray-100 pt-2">
                <span className="font-bold text-gray-700">Total Paid</span>
                <span className="font-black" style={{ color: '#2b2240' }}>{fmt(newTotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Remaining</span>
                <span className={`font-bold ${remaining === 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {remaining === 0 ? '✓ Fully Paid' : fmt(remaining)}
                </span>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────

type FilterStatus = 'All' | 'Fully Paid' | 'Partial' | 'Ran Away' | 'Class Over';
const FILTER_STATUSES: FilterStatus[] = ['All', 'Fully Paid', 'Partial', 'Ran Away', 'Class Over'];

const AdminPaymentTracker: React.FC = () => {
  const { session } = useTracker();
  const navigate    = useNavigate();
  const [params, setParams] = useSearchParams();
  const param = (k: string) => params.get(k) || '';

  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [spNames, setSpNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [deduping, setDeduping] = useState(false);
  // Filters — the joining dates, the salesperson, the status a tile picks and the search — kept in the page address
  const [period, setPeriod]             = useState<Period>(() => periodFromParams(params, 'all'));
  const [sales, setSales]               = useState(() => param('sales'));
  const [filterStatus, setFilterStatus] = useState<FilterStatus>(() => (FILTER_STATUSES.includes(param('status') as FilterStatus) ? param('status') as FilterStatus : 'All'));
  const [search, setSearch]             = useState(() => param('q'));
  const [editing, setEditing]   = useState<PaymentRecord | null>(null);
  const [dating, setDating]     = useState<PaymentRecord | null>(null);
  // The student a receipt is being sent to — by rowIndex, so the window shows
  // their latest totals; amount / recorded when it comes from Update Payment
  const [receipting, setReceipting] = useState<{ rowIndex: string; amount?: number; recorded?: boolean } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [menuRowIndex, setMenuRowIndex] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [classOverBusy, setClassOverBusy] = useState<string | null>(null);
  const [today, setToday] = useState(todayIST);

  const adminPassword = session?.kind === 'admin' ? session.adminPassword : '';

  useEffect(() => {
    const next = new URLSearchParams(periodParams(period));
    if (sales) next.set('sales', sales);
    if (filterStatus !== 'All') next.set('status', filterStatus);
    if (search.trim()) next.set('q', search.trim());
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [period, sales, filterStatus, search, params, setParams]);

  const load = useCallback(async () => {
    if (!adminPassword) return;
    setLoading(true);
    try {
      const [payRes, spRes] = await Promise.all([
        getPaymentData(adminPassword),
        getSalespeople(adminPassword),
      ]);
      setRecords(payRes.records ?? []);
      if (payRes.today) setToday(payRes.today);
      setSpNames((spRes.salespeople ?? []).filter((s: any) => s.status !== 'Inactive').map((s: any) => s.name));
    } catch (err: any) {
      toast.error('Failed to load: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!session || session.kind !== 'admin') navigate('/tracker/admin/login', { replace: true });
  }, [session, navigate]);

  if (!session || session.kind !== 'admin') return null;

  const handleSaved = (rowIndex: string, balancePaid: number, newTotal: number, sessionType: string, salesPerson: string, courseFee: number, enrollDate: string) => {
    // A payment just recorded → offer to email the receipt for it
    const before = records.find(r => r.rowIndex === rowIndex);
    const paidNow = before ? newTotal - before.totalPaid : 0;
    if (before && paidNow > 0 && courseFee > 0) {
      toast.success(
        <span>
          Payment updated for {before.name}.{' '}
          <button onClick={() => setReceipting({ rowIndex, amount: paidNow, recorded: true })} className="font-bold underline underline-offset-2">
            🧾 Email a receipt for {fmt(paidNow)}
          </button>
        </span>,
        { autoClose: 10000 },
      );
    } else {
      toast.success(`Payment updated for ${before?.name ?? 'the student'}`);
    }
    setRecords(prev => prev.map(r => {
      if (r.rowIndex !== rowIndex) return r;
      const balance = Math.max(0, courseFee - newTotal);
      const status: PaymentRecord['status'] =
        (r.status === 'Class Over' || r.status === 'Ran Away') ? r.status
        : newTotal >= courseFee ? 'Fully Paid' : 'Partial';
      // A payment recorded after the date was asked closes it (the server does the same)
      const nextPayment = newTotal > r.totalPaid || balance === 0 ? null : r.nextPayment;
      const mode = normalizeSession(sessionType);
      return {
        ...r, balancePaid, totalPaid: newTotal, balance, courseFee, mode, sessionType: mode, sessionCategory: mode,
        salesPerson, status, enrollDate, joined: enrollDate || r.joined, nextPayment,
      };
    }));
    refreshAdminNotifs();
  };

  // A receipt went out — when its amount was added to their payments, the
  // row's totals change (and an open payment date closes, as on the server)
  const handleReceiptSent = (rowIndex: string, p: { balancePaid: number; totalPaid: number; courseFee: number }) => {
    setRecords(prev => prev.map(r => {
      if (r.rowIndex !== rowIndex || p.totalPaid === r.totalPaid) return r;
      const balance = Math.max(0, p.courseFee - p.totalPaid);
      const status: PaymentRecord['status'] =
        (r.status === 'Class Over' || r.status === 'Ran Away') ? r.status
        : p.courseFee > 0 && p.totalPaid >= p.courseFee ? 'Fully Paid' : 'Partial';
      return { ...r, balancePaid: p.balancePaid, totalPaid: p.totalPaid, courseFee: p.courseFee, balance, status, nextPayment: null };
    }));
    refreshAdminNotifs();
  };

  // The next payment date changed in the Payment date window
  const handleDateChanged = (rowIndex: string, next: NextPayment | null) => {
    if (next) setRecords(prev => prev.map(r => (r.rowIndex === rowIndex ? { ...r, nextPayment: next } : r)));
    else load();
    refreshAdminNotifs();
  };

  const handleMarkRunAway = async (record: PaymentRecord, flag: boolean) => {
    try {
      await markRunAway(adminPassword, record.rowIndex, flag);
      setRecords(prev => prev.map(r =>
        r.rowIndex === record.rowIndex
          ? { ...r, status: flag ? 'Ran Away' : 'Partial', verificationStatus: flag ? 'Ran Away' : '', nextPayment: flag ? null : r.nextPayment }
          : r
      ));
      toast.success(flag ? `${record.name} marked Ran Away` : `${record.name} moved back to Partial`);
      if (flag) refreshAdminNotifs();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleClassOver = async (record: PaymentRecord, undo: boolean) => {
    setMenuRowIndex(null);
    setClassOverBusy(record.rowIndex);
    try {
      await setPaymentClassOver(adminPassword, record.rowIndex, undo);
      setRecords(prev => prev.map(r =>
        r.rowIndex === record.rowIndex
          ? { ...r, status: undo ? 'Partial' : 'Class Over', verificationStatus: undo ? '' : 'Class Over' }
          : r
      ));
      toast.success(undo ? `${record.name} moved back to Partial` : `${record.name} marked Class Over`);
    } catch (err: any) { toast.error(err.message); }
    finally { setClassOverBusy(null); }
  };

  const handleToggleIncentive = async (record: PaymentRecord) => {
    setMenuRowIndex(null);
    try {
      const res = await markIncentivePaid(adminPassword, record.rowIndex, record.incentivePaid);
      setRecords(prev => prev.map(r =>
        r.rowIndex === record.rowIndex ? { ...r, incentivePaid: res.incentivePaid } : r
      ));
      toast.success(res.incentivePaid ? `Incentive marked as given for ${record.name}` : `Incentive unmarked for ${record.name}`);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDelete = async (record: PaymentRecord) => {
    if (!window.confirm(`Delete payment record for "${record.name}"? This is permanent.`)) return;
    setDeletingId(record.rowIndex);
    try {
      await deletePaymentRecord(adminPassword, record.rowIndex as any);
      setRecords(prev => prev.filter(r => r.rowIndex !== record.rowIndex));
      toast.success(`Deleted ${record.name}`);
    } catch (err: any) { toast.error(err.message); }
    finally { setDeletingId(null); }
  };

  const handleDedup = async () => {
    if (!window.confirm('Remove duplicate rows, keeping the highest payment? This is permanent.')) return;
    setDeduping(true);
    try {
      const res = await deduplicatePayments(adminPassword);
      toast.success(`Removed ${res.removed} duplicate${res.removed !== 1 ? 's' : ''}`);
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setDeduping(false); }
  };

  // ── Filters — the tiles and revenue panel follow the joining dates and the
  // salesperson; the table also follows the status a tile picks and the search ──
  const scoped = records.filter(r => (period.preset === 'all' || within(joinedOf(r), period)) && matchesSales(r, sales));
  const visible = scoped.filter(r => (filterStatus === 'All' || r.status === filterStatus) && matchesSearch(r, search));

  // ── Stats ──
  const total      = scoped.length;
  const fullyPaid  = scoped.filter(r => r.status === 'Fully Paid').length;
  const partial    = scoped.filter(r => r.status === 'Partial').length;
  const ranAway    = scoped.filter(r => r.status === 'Ran Away').length;
  const classOver  = scoped.filter(r => r.status === 'Class Over').length;
  const onlineCount  = scoped.filter(r => modeOf(r) === 'online').length;
  const offlineCount = scoped.length - onlineCount;

  // ── Next payment dates due today / overdue (not affected by the date filter) ──
  const dueNow = records
    .filter(r => { const d = dueDateOf(r); return d && d <= today && r.balance > 0; })
    .sort((a, b) => dueDateOf(b).localeCompare(dueDateOf(a)));   // today first, then most recently due
  const dueTodayCount  = dueNow.filter(r => dueDateOf(r) === today).length;
  const dateRequests   = records.filter(r => r.nextPayment?.status === 'Pending').length;

  const activeForRevenue = scoped.filter(r => r.status !== 'Ran Away');
  const totalExpected    = activeForRevenue.reduce((s, r) => s + r.courseFee, 0);
  const totalCollected   = scoped.reduce((s, r) => s + r.totalPaid, 0);
  const totalPending     = activeForRevenue.reduce((s, r) => s + r.balance, 0);
  const ranAwayAmount    = scoped.filter(r => r.status === 'Ran Away').reduce((s, r) => s + r.balance, 0);

  // ── Dropdown options ──
  const allSalespeople = distinctNames([...spNames, ...records.map(r => r.salesPerson)]);
  const salesNames     = distinctNames(records.map(r => r.salesPerson));   // the filter: only names that have students

  const receiptRecord = receipting ? records.find(r => r.rowIndex === receipting.rowIndex) : undefined;

  const active: ActiveFilter[] = [
    ...(period.preset !== 'all' ? [{ label: `Joined: ${period.from ? fmtRange(period.from, period.to) : `up to ${fmtDay(period.to)}`}`, onRemove: () => setPeriod(presetPeriod('all')) }] : []),
    ...(sales ? [{ label: `Salesperson: ${sales === 'none' ? 'none' : sales}`, onRemove: () => setSales('') }] : []),
    ...(filterStatus !== 'All' ? [{ label: filterStatus, onRemove: () => setFilterStatus('All') }] : []),
    ...(search.trim() ? [{ label: `“${search.trim()}”`, onRemove: () => setSearch('') }] : []),
  ];
  const clearAll = () => { setPeriod(presetPeriod('all')); setSales(''); setFilterStatus('All'); setSearch(''); };

  return (
    <TrackerLayout>
      <style>{`@keyframes payMenuIn{from{opacity:0;transform:scale(.95) translateY(-4px);}to{opacity:1;transform:scale(1) translateY(0);}}`}</style>
      <div className="space-y-5 pb-8">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-1">
            <h1 className="text-2xl font-black" style={{ color: '#2b2240' }}>Payment Tracker</h1>
            <p className="text-xs text-gray-400 mt-0.5">{total} students · {fmt(totalCollected)} collected · {fmt(totalPending)} pending</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={load}
              className="px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 text-gray-500 hover:bg-gray-50 shadow-sm">
              Refresh
            </button>
            <button onClick={handleDedup} disabled={deduping}
              className="px-3 py-2 rounded-xl text-xs font-semibold border border-red-200 text-red-500 hover:bg-red-50 shadow-sm disabled:opacity-50">
              {deduping ? 'Cleaning…' : 'Remove Duplicates'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="w-10 h-10 rounded-full border-4 border-gray-100 border-t-[#2b2240] animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-400">Loading payment data…</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Payments due today / overdue (next payment dates) ── */}
            {(dueNow.length > 0 || dateRequests > 0) && (
              <div className="bg-white rounded-2xl border-2 shadow-sm overflow-hidden"
                style={{ borderColor: dueTodayCount > 0 ? '#86efac' : dueNow.length > 0 ? '#fca5a5' : '#fde68a' }}>
                <div className={`px-5 py-3 flex flex-wrap items-center justify-between gap-2 ${dueNow.length > 0 ? 'border-b border-gray-100' : ''}`}
                  style={{ background: dueTodayCount > 0 ? '#f0fdf4' : dueNow.length > 0 ? '#fef2f2' : '#fffbeb' }}>
                  <p className="text-sm font-black" style={{ color: '#2b2240' }}>
                    📅 Today's payments
                    {dueTodayCount > 0
                      ? <span className="ml-2 text-xs font-bold text-green-700">{dueTodayCount} due today</span>
                      : <span className="ml-2 text-xs font-semibold text-gray-400">none due today</span>}
                    {dueNow.length - dueTodayCount > 0 && (
                      <span className="ml-2 text-xs font-bold text-red-600">{dueNow.length - dueTodayCount} overdue</span>
                    )}
                  </p>
                  <button onClick={() => navigate('/tracker/admin/payment-dates')}
                    className="text-xs font-bold px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">
                    {dateRequests > 0 ? `🙋 ${dateRequests} date request${dateRequests === 1 ? '' : 's'} to approve →` : 'Manage payment dates →'}
                  </button>
                </div>
                {dueNow.length > 0 && (
                  <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
                    {dueNow.map(r => {
                      const due = dueDateOf(r);
                      return (
                        <div key={r.rowIndex} className="px-5 py-2.5 flex items-center gap-3 flex-wrap">
                          <div className="flex-1 min-w-[160px]">
                            <p className="text-sm font-bold" style={{ color: '#2b2240' }}>{r.name}</p>
                            <p className="text-[11px] text-gray-400">{[r.phone, r.course].filter(Boolean).join(' · ')}</p>
                          </div>
                          <span className="text-xs font-bold px-2 py-1 rounded-lg"
                            style={due === today ? { background: '#dcfce7', color: '#15803d' } : { background: '#fee2e2', color: '#b91c1c' }}>
                            {due === today ? 'Due today' : `Overdue · was due ${fmtShortDay(due)}`}
                          </span>
                          <span className="text-sm font-black text-red-600 w-24 text-right">{fmt(r.balance)}</span>
                          <button onClick={() => setDating(r)} title="Give more time, or set a new date"
                            className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">
                            📅 Change date
                          </button>
                          <button onClick={() => setEditing(r)}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                            style={{ background: '#2b2240' }}>
                            Record payment
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Filters ── */}
            <FilterCard active={active} onClearAll={clearAll}
              showing={<>Showing <b className="text-gray-700">{visible.length}</b> of {records.length} students</>}>
              <div className="flex flex-wrap items-end gap-3">
                <SelectFilter label="Salesperson" value={sales} onChange={setSales} allLabel="All salespeople"
                  options={[...salesNames.map(s => ({ value: s, label: s })), { value: 'none', label: 'No salesperson' }]} />
                <SearchFilter label="Search" value={search} onChange={setSearch}
                  placeholder="Name, phone, email, course, sales person or mentor" />
              </div>
              <PeriodPicker label="Joined" value={period} onChange={setPeriod} presets={['all', 'month', 'lastMonth', '90d', 'year']} dates />
            </FilterCard>

            {/* ── Stats grid ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Tile label="Total Enrolled" value={total} icon="👥"
                sub={`${onlineCount} online · ${offlineCount} offline`} />
              <Tile label="Fully Paid" value={fullyPaid} icon="✅" accent="#16a34a"
                sub={`${total ? Math.round(fullyPaid / total * 100) : 0}% of enrolled`}
                active={filterStatus === 'Fully Paid'} onClick={() => setFilterStatus(s => s === 'Fully Paid' ? 'All' : 'Fully Paid')} />
              <Tile label="Partial" value={partial} icon="⏳" accent="#d97706"
                sub="Paying, not full"
                active={filterStatus === 'Partial'} onClick={() => setFilterStatus(s => s === 'Partial' ? 'All' : 'Partial')} />
              <Tile label="Class Over" value={classOver} icon="🎓" accent="#0284c7"
                sub="Completed training"
                active={filterStatus === 'Class Over'} onClick={() => setFilterStatus(s => s === 'Class Over' ? 'All' : 'Class Over')} />
              <Tile label="Ran Away" value={ranAway} icon="🚨" accent="#db2777"
                sub={ranAwayAmount > 0 ? `${fmt(ranAwayAmount)} unpaid` : 'No loss'}
                active={filterStatus === 'Ran Away'} onClick={() => setFilterStatus(s => s === 'Ran Away' ? 'All' : 'Ran Away')} />
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm col-span-2 sm:col-span-1 lg:col-span-1">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: '#9ca3af' }}>Session Split</p>
                <div className="flex gap-3">
                  <div className="flex-1 text-center">
                    <div className="text-lg font-black" style={{ color: '#2b2240' }}>{onlineCount}</div>
                    <div className="text-[10px] text-gray-400">Online</div>
                  </div>
                  <div className="w-px bg-gray-100" />
                  <div className="flex-1 text-center">
                    <div className="text-lg font-black" style={{ color: '#7c3aed' }}>{offlineCount}</div>
                    <div className="text-[10px] text-gray-400">Offline</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Revenue panel ── */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">Revenue Overview</p>
              <div className="flex flex-col sm:flex-row gap-5">
                <RevBar label="Expected" value={totalExpected} total={totalExpected} color="#2b2240" icon="🎯" />
                <div className="w-px bg-gray-100 hidden sm:block" />
                <RevBar label="Collected" value={totalCollected} total={totalExpected} color="#16a34a" icon="💰" />
                <div className="w-px bg-gray-100 hidden sm:block" />
                <RevBar label="Pending" value={totalPending} total={totalExpected} color="#dc2626" icon="⏳" />
                {ranAwayAmount > 0 && (
                  <>
                    <div className="w-px bg-gray-100 hidden sm:block" />
                    <RevBar label="Ran Away Loss" value={ranAwayAmount} total={totalExpected} color="#db2777" icon="🚨" />
                  </>
                )}
              </div>
            </div>

            {/* ── Filters ── */}
            {/* ── Table ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg,#2b2240,#3d2f5c)' }}>
                      {['#', 'Student', 'Course', 'Mode', 'Classes', 'Joined', 'Sales By', 'Fee', 'Reg', 'Balance Paid', 'Total Paid', 'Due', 'Status', 'Actions'].map(h => (
                        <th key={h} className="px-3 py-3 text-left text-[10px] font-bold text-white/80 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {visible.length === 0 ? (
                      <tr><td colSpan={14} className="text-center py-16 text-gray-400">
                        <div className="text-3xl mb-2">🔍</div>
                        <p className="font-semibold">No records match your filters</p>
                      </td></tr>
                    ) : visible.map((r, idx) => {
                      const ss       = STATUS_STYLE[r.status] ?? STATUS_STYLE['Partial'];
                      const isOnline = modeOf(r) === 'online';
                      return (
                        <tr key={r.rowIndex}
                          className={`hover:bg-gray-50/70 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                          <td className="px-3 py-3 text-[11px] text-gray-400 font-mono">{idx + 1}</td>

                          {/* Student */}
                          <td className="px-3 py-3">
                            <div className="font-bold text-sm whitespace-nowrap" style={{ color: '#2b2240' }}>{r.name}</div>
                            {r.phone && <div className="text-[10px] text-gray-400 mt-0.5">{r.phone}</div>}
                          </td>

                          {/* Course */}
                          <td className="px-3 py-3 max-w-[130px]">
                            <span className="text-xs text-gray-600 line-clamp-2">{r.course || '—'}</span>
                          </td>

                          {/* Mode */}
                          <td className="px-3 py-3">
                            <span className="text-[10px] px-2 py-1 rounded-lg font-bold"
                              style={isOnline
                                ? { background: '#e0f2fe', color: '#075985' }
                                : { background: '#ede9fe', color: '#5b21b6' }}>
                              {isOnline ? '🌐 Online' : '🏫 Offline'}
                            </span>
                          </td>

                          {/* Classes */}
                          <td className="px-3 py-3 text-center">
                            {(r.classCount ?? 0) > 0 ? (
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-black"
                                style={{ background: '#f0fdf4', color: '#15803d', border: '2px solid #bbf7d0' }}>
                                {r.classCount}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-sm">—</span>
                            )}
                          </td>

                          {/* Joined */}
                          <td className="px-3 py-3 text-[11px] text-gray-500 whitespace-nowrap">
                            {r.enrollDate || <span className="text-gray-300">—</span>}
                          </td>

                          {/* Sales */}
                          <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">{r.salesPerson || <span className="text-gray-300">—</span>}</td>

                          {/* Fee */}
                          <td className="px-3 py-3 text-xs font-bold whitespace-nowrap" style={{ color: '#2b2240' }}>{fmt(r.courseFee)}</td>

                          {/* Reg */}
                          <td className="px-3 py-3 text-xs font-semibold text-green-700 whitespace-nowrap">{fmt(r.regPaid)}</td>

                          {/* Balance paid */}
                          <td className="px-3 py-3 text-xs font-semibold whitespace-nowrap">
                            {r.balancePaid > 0
                              ? <span className="text-green-700">{fmt(r.balancePaid)}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>

                          {/* Total paid — and the receipt for a payment (a chip, so the table gets no wider) */}
                          <td className="px-3 py-3 text-sm font-black whitespace-nowrap" style={{ color: '#2b2240' }}>
                            {fmt(r.totalPaid)}
                            <button onClick={() => setReceipting({ rowIndex: r.rowIndex })} title="Type in a payment and email the receipt"
                              className="block mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full w-fit whitespace-nowrap hover:ring-2 hover:ring-offset-1 hover:ring-purple-200"
                              style={{ background: '#ede9fe', color: '#2b2240' }}>
                              🧾 send receipt
                            </button>
                          </td>

                          {/* Due */}
                          <td className="px-3 py-3 text-xs font-bold whitespace-nowrap">
                            {r.balance === 0
                              ? <span className="text-green-500">✓</span>
                              : <span className="text-red-600">{fmt(r.balance)}</span>}
                            <NextPaymentChip r={r} today={today} onOpen={() => setDating(r)} />
                          </td>

                          {/* Status */}
                          <td className="px-3 py-3">
                            <div className="flex flex-col items-start gap-0.5">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap"
                                style={{ background: ss.bg, color: ss.color }}>
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ss.dot }} />
                                {r.status}
                              </span>
                              {r.status === 'Fully Paid' && r.salesPerson && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                                  style={r.incentivePaid
                                    ? { background:'#f0fdf4', color:'#16a34a' }
                                    : { background:'#fef3c7', color:'#b45309' }}>
                                  {r.incentivePaid ? '💰 Given' : '⏳ Pending'}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Actions — popup menu */}
                          <td className="px-3 py-3">
                            <div className="relative">
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  if (menuRowIndex === r.rowIndex) {
                                    setMenuRowIndex(null); setMenuPos(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                    setMenuRowIndex(r.rowIndex);
                                  }
                                }}
                                disabled={deletingId === r.rowIndex || classOverBusy === r.rowIndex}
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 border border-gray-200 transition-all disabled:opacity-40"
                                title="Actions">
                                {deletingId === r.rowIndex || classOverBusy === r.rowIndex ? (
                                  <span className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin block" />
                                ) : (
                                  <svg viewBox="0 0 4 16" className="w-1 h-4 fill-current"><circle cx="2" cy="2" r="1.5"/><circle cx="2" cy="8" r="1.5"/><circle cx="2" cy="14" r="1.5"/></svg>
                                )}
                              </button>

                              {menuRowIndex === r.rowIndex && menuPos && (
                                <>
                                  {/* backdrop */}
                                  <div className="fixed inset-0 z-[100]" onClick={() => { setMenuRowIndex(null); setMenuPos(null); }} />
                                  {/* popup — fixed so it escapes overflow:hidden parents */}
                                  <div className="z-[101] bg-white rounded-xl shadow-xl border border-gray-100 py-1 min-w-[160px] overflow-hidden"
                                    style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, animation: 'payMenuIn .15s ease both' }}>
                                    {/* Update */}
                                    <button onClick={() => { setMenuRowIndex(null); setEditing(r); }}
                                      className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-amber-50 transition-colors"
                                      style={{ color: '#92400e' }}>
                                      <span className="text-base">✏️</span> Update Payment
                                    </button>

                                    {/* Receipt — type in a payment and email the receipt */}
                                    <button onClick={() => { setMenuRowIndex(null); setReceipting({ rowIndex: r.rowIndex }); }}
                                      className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-purple-50 transition-colors"
                                      style={{ color: '#2b2240' }}>
                                      <span className="text-base">🧾</span> Send Receipt
                                    </button>

                                    {/* Next payment date — ask the student, or set / change it */}
                                    {(r.nextPayment || canDate(r)) && (
                                      <button onClick={() => { setMenuRowIndex(null); setDating(r); }}
                                        className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-purple-50 transition-colors"
                                        style={{ color: '#5b21b6' }}>
                                        <span className="text-base">📅</span>
                                        {r.nextPayment?.status === 'Pending' ? 'Approve payment date'
                                          : r.nextPayment && DATED_PAYMENT_STATUSES.includes(r.nextPayment.status) ? 'Change payment date'
                                          : r.nextPayment ? 'Remind / set payment date'
                                          : 'Ask for / set payment date'}
                                      </button>
                                    )}

                                    {/* Ran Away / Undo */}
                                    {r.status === 'Ran Away' ? (
                                      <button onClick={() => { setMenuRowIndex(null); handleMarkRunAway(r, false); }}
                                        className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-amber-50 transition-colors"
                                        style={{ color: '#d97706' }}>
                                        <span className="text-base">↩️</span> Undo Ran Away
                                      </button>
                                    ) : (r.status === 'Partial' || r.status === 'Fully Paid') && (
                                      <button onClick={() => { setMenuRowIndex(null); handleMarkRunAway(r, true); }}
                                        className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-pink-50 transition-colors"
                                        style={{ color: '#9d174d' }}>
                                        <span className="text-base">🚨</span> Mark Ran Away
                                      </button>
                                    )}

                                    {/* Deactivate / Class Over */}
                                    {r.status === 'Class Over' ? (
                                      <button onClick={() => handleClassOver(r, true)}
                                        className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-blue-50 transition-colors"
                                        style={{ color: '#0369a1' }}>
                                        <span className="text-base">↩️</span> Reactivate Student
                                      </button>
                                    ) : r.status !== 'Ran Away' && (
                                      <button onClick={() => handleClassOver(r, false)}
                                        className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-blue-50 transition-colors"
                                        style={{ color: '#0284c7' }}>
                                        <span className="text-base">🎓</span> Deactivate (Class Over)
                                      </button>
                                    )}

                                    {/* Incentive toggle — only for Fully Paid with a salesperson */}
                                    {r.status === 'Fully Paid' && r.salesPerson && (
                                      <>
                                        <div className="mx-3 my-1 border-t border-gray-100" />
                                        <button onClick={() => handleToggleIncentive(r)}
                                          className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-green-50 transition-colors"
                                          style={{ color: r.incentivePaid ? '#6b7280' : '#16a34a' }}>
                                          <span className="text-base">{r.incentivePaid ? '↩️' : '💰'}</span>
                                          {r.incentivePaid ? 'Undo Incentive Given' : 'Mark Incentive Given'}
                                        </button>
                                      </>
                                    )}

                                    <div className="mx-3 my-1 border-t border-gray-100" />

                                    {/* Delete */}
                                    <button onClick={() => { setMenuRowIndex(null); handleDelete(r); }}
                                      className="w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2.5 hover:bg-red-50 transition-colors"
                                      style={{ color: '#dc2626' }}>
                                      <span className="text-base">🗑️</span> Delete Record
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {visible.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
                  <span className="text-xs text-gray-400">{visible.length} record{visible.length !== 1 ? 's' : ''}</span>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>Collected: <strong className="text-green-600">{fmt(visible.reduce((s, r) => s + r.totalPaid, 0))}</strong></span>
                    <span>Pending: <strong className="text-red-600">{fmt(visible.reduce((s, r) => s + r.balance, 0))}</strong></span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {editing && (
        <UpdateModal
          record={editing}
          adminPassword={adminPassword}
          allSalespeople={allSalespeople}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {dating && (
        <PaymentDateModal
          record={dating}
          adminPassword={adminPassword}
          today={today}
          onClose={() => setDating(null)}
          onChanged={handleDateChanged}
        />
      )}

      {receiptRecord && (
        <ReceiptModal
          key={`${receiptRecord.rowIndex}:${receipting?.amount ?? ''}`}
          record={receiptRecord}
          adminPassword={adminPassword}
          today={today}
          amount={receipting?.amount}
          alreadyRecorded={receipting?.recorded}
          onClose={() => setReceipting(null)}
          onSent={handleReceiptSent}
        />
      )}
    </TrackerLayout>
  );
};

export default AdminPaymentTracker;
